"use client";

import type { ReactNode } from "react";
import { Heading } from "@farm-junction/design-library";
import { Accordion } from "@/component/ui/Accordion";
import Schema from "./Schema";

type FAQ = {
    id?: string;
    _id?: string;
    question: string;
    answer: ReactNode;
};

export type FAQItem = FAQ;

type FAQSectionLibProps = {
    items: FAQ[];
    title?: string;
    description?: string;
    multi?: boolean;
    /** Set false when the page already emits FAQPage JSON-LD from the server. */
    includeSchema?: boolean;
};

const answerToText = (answer: ReactNode) => {
    if (typeof answer === "string" || typeof answer === "number") return String(answer);
    return "";
};

export default function FAQSectionLib({
    items,
    title,
    description,
    includeSchema = true,
}: FAQSectionLibProps) {
    const faqs = items ?? [];

    if (!faqs.length) return null;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
                "@type": "Answer",
                text: answerToText(faq.answer),
            },
        })),
    };

    return (
        <>
            {includeSchema ? <Schema schema={faqSchema} /> : null}
            <section id="faqs" className="mt-3">
                <div className="py-4 mb-5 md:mb-8">
                    <div className="mb-6">
                        <Heading
                            variant="h2"
                            level={2}
                            className="!mb-1 text-[20px] leading-6 font-semibold text-gray-900 md:font-medium"
                        >
                            {title ?? "Frequently Asked Questions"}
                        </Heading>
                        <p className="mt-1 text-sm text-gray-500">
                            {description ?? "Find answers to common questions about buying and selling cars on Car Junction"}
                        </p>
                    </div>

                    <Accordion
                        defaultOpenIndex={0}
                        items={faqs.map((faq) => ({
                            title: faq.question,
                            content:
                                typeof faq.answer === "string" ? (
                                    <div
                                        className="[&_a]:text-blue-600 [&_a:hover]:underline"
                                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                                    />
                                ) : (
                                    faq.answer
                                ),
                        }))}
                    />
                </div>
            </section>
        </>
    );
}
